import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/UIKit";
import { fmtNumber } from "@/lib/format";
import {
  ResponsiveContainer, ComposedChart, Line, Area, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, BarChart, Bar,
} from "recharts";
import {
  Zap, TrendingUp, TrendingDown, Activity, RefreshCw, MapPin, Database,
  ArrowUp, ArrowDown, Waves, Wind, Sun, Factory,
} from "lucide-react";
import { toast } from "sonner";

const AREAS = ["NO1", "NO2", "NO3", "NO4", "NO5"];

const CONSUMPTION_GROUP_LABELS = {
  private: "Privathusholdning",
  cabin: "Hytter/fritid",
  commercial: "Næring",
  industry: "Industri",
  unknown: "Ukjent",
};
const PRODUCTION_ICONS = {
  hydro: Waves,
  wind: Wind,
  solar: Sun,
  thermal: Factory,
  unknown: Zap,
};

/**
 * Elhub markedsdata — offentlige aggregerte tall fra api.elhub.no
 */
export default function ElhubData() {
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState(null);
  const [area, setArea] = useState("NO1");
  const [loading, setLoading] = useState(false);

  const load = async (a = area) => {
    setLoading(true);
    try {
      const [{ data: s }, { data: ser }] = await Promise.all([
        api.get("/elhub-open/summary"),
        api.get("/elhub-open/series", { params: { area: a } }),
      ]);
      setSummary(s);
      setSeries(ser);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente Elhub-data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(area); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [area]);

  const areaData = summary?.areas?.find((a) => a.area === area);

  return (
    <div data-testid="elhub-page">
      <PageHeader
        title="Elhub markedsdata"
        subtitle="Live aggregerte tall fra api.elhub.no — forbruk, produksjon og balanse per prisområde"
        actions={
          <button onClick={() => load(area)} disabled={loading}
            data-testid="elhub-refresh"
            className="h-9 px-3 rounded-md border border-zinc-800 hover:border-zinc-700 text-zinc-300 text-xs flex items-center gap-1.5">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Oppdater
          </button>
        }
      />

      {/* Info-boks om åpen API */}
      <div className="mb-5 border border-zinc-900 bg-zinc-950/40 rounded-md p-3.5 flex items-start gap-3">
        <Database className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
        <div className="text-xs text-zinc-400 leading-relaxed">
          Data hentes fra Elhubs offentlige Energy Data API (v0) — ingen sertifikat
          eller kunde-avtale nødvendig. Cacheholdes 15 min. Brukes til markedsinnsikt,
          benchmark og prognoser — <span className="text-zinc-300">ikke BRS-meldinger</span>{" "}
          (som krever separat Elhub-tilgang).
        </div>
      </div>

      {/* Prisområde-velger */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="elhub-area-selector">
        <span className="text-[10px] uppercase tracking-widest text-zinc-500 mr-2">Prisområde</span>
        {AREAS.map((a) => (
          <button key={a} onClick={() => setArea(a)}
            data-testid={`area-${a}`}
            className={`h-9 px-4 rounded-md border text-sm font-medium transition-colors ${
              area === a
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                : "border-zinc-900 hover:border-zinc-800 text-zinc-400 hover:text-zinc-100"
            }`}>
            {a}
          </button>
        ))}
      </div>

      {!summary ? (
        <div className="p-8"><Spinner label="Henter fra Elhub…" /></div>
      ) : !areaData ? (
        <Empty title="Ingen data" subtitle={`Elhub returnerte ingen data for ${area}`} />
      ) : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
            <KpiCard
              icon={ArrowDown}
              label="Totalt forbruk"
              value={fmtNumber(areaData.total_consumption_kwh)}
              unit="kWh"
              subtitle={`Siden ${fmtTime(areaData.latest_data_timestamp)}`}
              accent="rose"
              testid="kpi-consumption"
            />
            <KpiCard
              icon={ArrowUp}
              label="Total produksjon"
              value={fmtNumber(areaData.total_production_kwh)}
              unit="kWh"
              subtitle={`I ${area}`}
              accent="emerald"
              testid="kpi-production"
            />
            <KpiCard
              icon={areaData.net_balance_kwh >= 0 ? TrendingUp : TrendingDown}
              label="Nettobalanse"
              value={fmtNumber(areaData.net_balance_kwh)}
              unit="kWh"
              subtitle={areaData.net_balance_kwh >= 0 ? "Overskudd" : "Underskudd"}
              accent={areaData.net_balance_kwh >= 0 ? "emerald" : "amber"}
              testid="kpi-balance"
            />
          </div>

          {/* Chart: hourly production vs consumption */}
          <Card className="p-5 mb-5" testid="elhub-chart">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-heading font-medium text-zinc-50">Forbruk vs produksjon — siste 7 dager</div>
                <div className="text-xs text-zinc-500">Time-for-time i {area}. Kilde: Elhub.</div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <LegendDot color="rgb(244,63,94)" label="Forbruk" />
                <LegendDot color="rgb(52,211,153)" label="Produksjon" />
              </div>
            </div>
            <div className="h-80">
              <ResponsiveContainer>
                <ComposedChart data={series?.points || []}>
                  <defs>
                    <linearGradient id="prod" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="rgb(52,211,153)" stopOpacity="0.35" />
                      <stop offset="100%" stopColor="rgb(52,211,153)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#1f1f1f" strokeDasharray="2 4" vertical={false} />
                  <XAxis dataKey="time" tickFormatter={fmtTimeShort} stroke="#666" tick={{ fontSize: 10 }} minTickGap={40} />
                  <YAxis stroke="#666" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1e6).toFixed(1)}M`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: "#0e0e0e", border: "1px solid #27272a", borderRadius: 6, fontSize: 12 }}
                    labelFormatter={fmtTime}
                    formatter={(v, k) => [`${fmtNumber(v)} kWh`, k === "production" ? "Produksjon" : "Forbruk"]}
                  />
                  <Area type="monotone" dataKey="production" fill="url(#prod)" stroke="rgb(52,211,153)" strokeWidth={1.5} />
                  <Line type="monotone" dataKey="consumption" stroke="rgb(244,63,94)" strokeWidth={1.5} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Split by group */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
            <Card className="p-5" testid="consumption-groups">
              <div className="font-heading font-medium text-zinc-50 mb-1">Forbruk fordelt på gruppe</div>
              <div className="text-xs text-zinc-500 mb-4">Aggregert siste tilgjengelige periode i {area}</div>
              <GroupBars data={areaData.consumption_by_group} color="rgb(244,63,94)" labels={CONSUMPTION_GROUP_LABELS} />
            </Card>
            <Card className="p-5" testid="production-groups">
              <div className="font-heading font-medium text-zinc-50 mb-1">Produksjon fordelt på kilde</div>
              <div className="text-xs text-zinc-500 mb-4">Aggregert siste tilgjengelige periode i {area}</div>
              <ProductionSources data={areaData.production_by_group} />
            </Card>
          </div>

          {/* All areas comparison */}
          <Card className="p-5" testid="all-areas">
            <div className="font-heading font-medium text-zinc-50 mb-1">Alle prisområder</div>
            <div className="text-xs text-zinc-500 mb-4">Nettobalanse per prisområde (positive tall = eksport-overskudd)</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {summary.areas.map((a) => (
                <button key={a.area} onClick={() => setArea(a.area)}
                  data-testid={`compare-${a.area}`}
                  className={`text-left border rounded-md p-3 transition-colors ${
                    a.area === area ? "border-emerald-500/40 bg-emerald-500/5" : "border-zinc-900 hover:border-zinc-800"
                  }`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <MapPin className="h-3 w-3 text-zinc-500" />
                    <span className="font-heading text-sm font-medium text-zinc-50">{a.area}</span>
                  </div>
                  <div className={`text-xs tabular-nums ${a.net_balance_kwh >= 0 ? "text-emerald-400" : "text-amber-400"}`}>
                    {a.net_balance_kwh >= 0 ? "+" : ""}{fmtNumber(a.net_balance_kwh / 1000)} MWh
                  </div>
                  <div className="text-[10px] text-zinc-600 mt-0.5">
                    P: {fmtNumber(a.total_production_kwh / 1e6)}M · F: {fmtNumber(a.total_consumption_kwh / 1e6)}M kWh
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, unit, subtitle, accent = "emerald", testid }) {
  const cmap = {
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
    rose: "text-rose-400 bg-rose-500/10 border-rose-500/20",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  };
  return (
    <Card className="p-5" testid={testid}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-medium">{label}</div>
        <div className={`h-8 w-8 rounded-md border grid place-items-center ${cmap[accent]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="font-heading text-3xl font-semibold tabular-nums text-zinc-50">{value}</span>
        <span className="text-sm text-zinc-500">{unit}</span>
      </div>
      {subtitle && <div className="text-xs text-zinc-500 mt-1.5">{subtitle}</div>}
    </Card>
  );
}

function GroupBars({ data, color, labels }) {
  const entries = Object.entries(data || {})
    .map(([k, v]) => ({ group: labels[k] || k, kwh: v, key: k }))
    .sort((a, b) => b.kwh - a.kwh);
  if (!entries.length) return <div className="text-sm text-zinc-500">Ingen data</div>;
  return (
    <div className="h-56">
      <ResponsiveContainer>
        <BarChart data={entries} layout="vertical" margin={{ left: 40 }}>
          <CartesianGrid stroke="#1f1f1f" strokeDasharray="2 4" horizontal={false} />
          <XAxis type="number" stroke="#666" tick={{ fontSize: 10 }} tickFormatter={(v) => `${(v/1e6).toFixed(1)}M`} />
          <YAxis type="category" dataKey="group" stroke="#999" tick={{ fontSize: 11 }} width={100} />
          <Tooltip
            contentStyle={{ backgroundColor: "#0e0e0e", border: "1px solid #27272a", borderRadius: 6, fontSize: 12 }}
            formatter={(v) => [`${fmtNumber(v)} kWh`, "Forbruk"]}
          />
          <Bar dataKey="kwh" fill={color} radius={[0, 3, 3, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ProductionSources({ data }) {
  const entries = Object.entries(data || {})
    .map(([k, v]) => ({ key: k, name: prodLabel(k), kwh: v, Icon: PRODUCTION_ICONS[k] || Zap }))
    .sort((a, b) => b.kwh - a.kwh);
  const total = entries.reduce((s, e) => s + e.kwh, 0) || 1;
  if (!entries.length) return <div className="text-sm text-zinc-500">Ingen produksjonsdata</div>;
  return (
    <div className="space-y-3">
      {entries.map((e) => {
        const pct = Math.round((e.kwh / total) * 100);
        return (
          <div key={e.key} data-testid={`prod-${e.key}`}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2 text-sm text-zinc-200">
                <e.Icon className="h-4 w-4 text-emerald-400" />
                <span>{e.name}</span>
              </div>
              <div className="text-xs text-zinc-400 tabular-nums">
                {fmtNumber(e.kwh)} kWh <span className="text-zinc-600 ml-1">({pct}%)</span>
              </div>
            </div>
            <div className="h-1.5 bg-zinc-900 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400" style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="flex items-center gap-1.5 text-zinc-400">
      <div className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}

function prodLabel(key) {
  return { hydro: "Vannkraft", wind: "Vindkraft", solar: "Solkraft", thermal: "Termisk", unknown: "Ukjent" }[key] || key;
}

function fmtTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("nb-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
function fmtTimeShort(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return `${d.getDate()}.${d.getMonth() + 1} ${String(d.getHours()).padStart(2, "0")}`;
  } catch { return ""; }
}
